import React, { useState, useEffect } from "react";
import Header from "../../components/header/Header";
import Footer from "../../components/footer/Footer";
import "./CreateRetailBusiness.css";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import { useNavigate, useSearchParams } from "react-router-dom";
import { API_URL } from "../../constant";
import {
  FaEye,
  FaEyeSlash,
  FaCamera,
  FaTimes,
  FaChevronLeft,
  FaChevronRight,
  FaCheck,
  FaSpinner,
  FaExclamationTriangle,
} from "react-icons/fa";
import ReCAPTCHA from "react-google-recaptcha";
import { isValidPostalCode } from "../../utils/postalCodeValidator";

const CreateRetailBusiness = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [captchaToken, setCaptchaToken] = useState(null); // recaptcha

  // Stepper state
  const [currentStep, setCurrentStep] = useState(1);
  const totalSteps = 3;

  // Form data state
  const [formData, setFormData] = useState({
    // Step 1: Personal Information
    first_name: "",
    last_name: "",
    email: "",
    phone_number: "",
    password: "",
    password_confirmation: "",
    showPassword: false,
    showConfirmPassword: false,

    // Step 2: Business Address
    country_name: "United Kingdom",
    region: "England - City of London",
    address_line1: "",
    address_line2: "",
    address_line3: "",
    city: "",
    postcode: "",

    // Step 3: Shop Details
    retail_name: "",
    retail_address: "",
    retail_type: "",
    retail_sub_type: "",
    website: "",
    signed_contract: false,
    status: "active",
  });

  // Photo upload states
  const [photo, setPhoto] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [photoError, setPhotoError] = useState("");

  // Retail types and sub-types state
  const [retailTypes, setRetailTypes] = useState([]);
  const [retailSubTypes, setRetailSubTypes] = useState([]);
  const [allRetailData, setAllRetailData] = useState({});
  const [loadingTypes, setLoadingTypes] = useState(false);
  const [loadingSubTypes, setLoadingSubTypes] = useState(false);
  const [typeLoadError, setTypeLoadError] = useState("");

  const [errors, setErrors] = useState({});
  const [message, setMessage] = useState({ text: "", type: "" });
  const [isLoading, setIsLoading] = useState(false);
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [validatingFields, setValidatingFields] = useState(false);

  // Get referral code from URL parameter
  const [referralCode, setReferralCode] = useState(null);

  // Fetch retail types on component mount
  useEffect(() => {
    fetchRetailTypes();

    // Initialize referral code
    const urlRef = searchParams.get("ref");
    const urlCountry = searchParams.get("country");

    if (urlCountry && regionOptions[urlCountry]) {
      setFormData((prev) => ({
        ...prev,
        country_name: urlCountry,
        region: regionOptions[urlCountry]?.[0] || prev.region,
      }));
    }

    if (urlRef) {
      localStorage.setItem("business_referral_code", urlRef);
      setReferralCode(urlRef);
    } else {
      const storedRef = localStorage.getItem("business_referral_code");
      if (storedRef) {
        setReferralCode(storedRef);
      }
    }
  }, [searchParams]);

  // captcha handler
  const onCaptchaChange = (token) => {
    setCaptchaToken(token);
  };

  // Fetch all retail types and sub-types from backend
  const fetchRetailTypes = async () => {
    setLoadingTypes(true);
    setTypeLoadError("");
    try {
      const response = await fetch(
        `${API_URL}/api/retail-types/registration-data`,
      );
      const data = await response.json();

      if (data.success && data.data) {
        setAllRetailData(data.data);
        // Extract unique types
        const types = Object.keys(data.data);
        setRetailTypes(types);

        if (types.length === 0) {
          setTypeLoadError(
            "No retail types available. Please contact administrator.",
          );
          // Set fallback types
          setRetailTypes([
            "Clothing",
            "Electronics",
            "Grocery",
            "Furniture",
            "Jewelry",
            "Sports",
            "Food",
            "Other",
          ]);
        }
      } else {
        setTypeLoadError(data.message || "Failed to load retail types");
        // Set fallback types
        setRetailTypes([
          "Clothing",
          "Electronics",
          "Grocery",
          "Furniture",
          "Jewelry",
          "Sports",
          "Food",
          "Other",
        ]);
      }
    } catch (error) {
      console.error("Error fetching retail types:", error);
      setTypeLoadError("Network error. Using default retail types.");
      // Set fallback types
      setRetailTypes([
        "Clothing",
        "Electronics",
        "Grocery",
        "Furniture",
        "Jewelry",
        "Sports",
        "Food",
        "Other",
      ]);
    } finally {
      setLoadingTypes(false);
    }
  };

  // Update sub-types when retail type changes
  useEffect(() => {
    if (formData.retail_type && allRetailData[formData.retail_type]) {
      setRetailSubTypes(allRetailData[formData.retail_type]);
    } else {
      setRetailSubTypes([]);
    }

    // Reset sub-type when type changes
    if (formData.retail_sub_type) {
      setFormData((prev) => ({
        ...prev,
        retail_sub_type: "",
      }));
    }
  }, [formData.retail_type, allRetailData]);

  // Photo upload handler
  const handlePhotoUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setPhotoError("");
    setErrors((prev) => ({ ...prev, photo: "" }));

    const validTypes = ["image/jpeg", "image/jpg", "image/png", "image/gif"];
    if (!validTypes.includes(file.type)) {
      const errorMsg = "Please select a valid image file (JPEG, JPG, PNG, GIF)";
      setPhotoError(errorMsg);
      setErrors((prev) => ({ ...prev, photo: errorMsg }));
      return;
    }

    const maxSize = 2 * 1024 * 1024;
    if (file.size > maxSize) {
      const errorMsg = "Image size should be less than 2MB";
      setPhotoError(errorMsg);
      setErrors((prev) => ({ ...prev, photo: errorMsg }));
      return;
    }

    const img = new Image();
    const objectUrl = URL.createObjectURL(file);

    img.onload = function () {
      setPhoto(file);
      const reader = new FileReader();
      reader.onload = (e) => {
        setPhotoPreview(e.target.result);
      };
      reader.readAsDataURL(file);
      URL.revokeObjectURL(objectUrl);
    };

    img.onerror = function () {
      const errorMsg = "The selected file is not a valid image";
      setPhotoError(errorMsg);
      setErrors((prev) => ({ ...prev, photo: errorMsg }));
      URL.revokeObjectURL(objectUrl);
    };

    img.src = objectUrl;
  };

  const handleRemovePhoto = () => {
    setPhoto(null);
    setPhotoPreview(null);
    setPhotoError("");
    setErrors((prev) => ({ ...prev, photo: "" }));
    const fileInput = document.getElementById("photo-upload");
    if (fileInput) {
      fileInput.value = "";
    }
  };

  // Region options
  const regionOptions = {
    // Africa
    Afghanistan: ["Asia"],
    Albania: ["Europe"],
    Algeria: ["Africa"],
    Andorra: ["Europe"],
    Angola: ["Africa"],
    Argentina: ["South America"],
    Armenia: ["Asia"],
    Australia: ["Oceania"],
    Austria: ["Europe"],
    Azerbaijan: ["Asia"],
    Bahamas: ["North America"],
    Bahrain: ["Asia"],
    Bangladesh: ["Asia"],
    Barbados: ["North America"],
    Belarus: ["Europe"],
    Belgium: ["Europe"],
    Belize: ["North America"],
    Benin: ["Africa"],
    Bhutan: ["Asia"],
    Bolivia: ["South America"],
    "Bosnia and Herzegovina": ["Europe"],
    Botswana: ["Africa"],
    Brazil: ["South America"],
    Brunei: ["Asia"],
    Bulgaria: ["Europe"],
    "Burkina Faso": ["Africa"],
    Burundi: ["Africa"],
    Cambodia: ["Asia"],
    Cameroon: ["Africa"],
    Canada: ["Alberta", "British Columbia", "Manitoba", "Ontario", "Quebec"],
    "Cape Verde": ["Africa"],
    "Central African Republic": ["Africa"],
    Chad: ["Africa"],
    Chile: ["South America"],
    China: ["Asia"],
    Colombia: ["South America"],
    Comoros: ["Africa"],
    Congo: ["Africa"],
    "Costa Rica": ["North America"],
    Croatia: ["Europe"],
    Cuba: ["North America"],
    Cyprus: ["Europe"],
    "Czech Republic": ["Europe"],
    Denmark: ["Europe"],
    Djibouti: ["Africa"],
    "Dominican Republic": ["North America"],
    Ecuador: ["South America"],
    Egypt: ["Africa"],
    "El Salvador": ["North America"],
    Estonia: ["Europe"],
    Ethiopia: ["Africa"],
    Finland: ["Europe"],
    France: ["Europe"],
    Germany: ["Europe"],
    Ghana: ["Africa"],
    Greece: ["Europe"],
    Guatemala: ["North America"],
    Haiti: ["North America"],
    Honduras: ["North America"],
    Hungary: ["Europe"],
    Iceland: ["Europe"],
    India: [
      "Andhra Pradesh",
      "Delhi",
      "Karnataka",
      "Maharashtra",
      "Tamil Nadu",
      "West Bengal",
    ],
    Indonesia: ["Asia"],
    Iran: ["Tehran", "Isfahan", "Fars", "Mazandaran", "Khorasan Razavi"],
    Iraq: ["Asia"],
    Ireland: ["Europe"],
    Israel: ["Asia"],
    Italy: ["Europe"],
    Jamaica: ["North America"],
    Japan: ["Asia"],
    Jordan: ["Asia"],
    Kazakhstan: ["Asia"],
    Kenya: ["Africa"],
    Kuwait: ["Asia"],
    Kyrgyzstan: ["Asia"],
    Laos: ["Asia"],
    Latvia: ["Europe"],
    Lebanon: ["Asia"],
    Lesotho: ["Africa"],
    Liberia: ["Africa"],
    Libya: ["Africa"],
    Lithuania: ["Europe"],
    Luxembourg: ["Europe"],
    Madagascar: ["Africa"],
    Malawi: ["Africa"],
    Malaysia: ["Asia"],
    Maldives: ["Asia"],
    Mali: ["Africa"],
    Malta: ["Europe"],
    Mexico: ["North America"],
    Moldova: ["Europe"],
    Mongolia: ["Asia"],
    Morocco: ["Africa"],
    Mozambique: ["Africa"],
    Myanmar: ["Asia"],
    Namibia: ["Africa"],
    Nepal: ["Asia"],
    Netherlands: ["Europe"],
    "New Zealand": ["Oceania"],
    Nicaragua: ["North America"],
    Niger: ["Africa"],
    Nigeria: ["Africa"],
    "North Korea": ["Asia"],
    Norway: ["Europe"],
    Oman: ["Asia"],
    Pakistan: ["Asia"],
    Panama: ["North America"],
    Paraguay: ["South America"],
    Peru: ["South America"],
    Philippines: ["Asia"],
    Poland: ["Europe"],
    Portugal: ["Europe"],
    Qatar: ["Asia"],
    Romania: ["Europe"],
    Russia: ["Europe"],
    Rwanda: ["Africa"],
    "Saudi Arabia": ["Asia"],
    Senegal: ["Africa"],
    Serbia: ["Europe"],
    Singapore: ["Asia"],
    Slovakia: ["Europe"],
    Slovenia: ["Europe"],
    Somalia: ["Africa"],
    "South Africa": ["Africa"],
    "South Korea": ["Asia"],
    Spain: ["Europe"],
    "Sri Lanka": [
      "Central Province",
      "Eastern Province",
      "Northern Province",
      "Southern Province",
      "Western Province",
      "North Central Province",
      "North Western Province",
      "Sabaragamuwa Province",
      "Uva Province",
    ],
    Sudan: ["Africa"],
    Sweden: ["Europe"],
    Switzerland: ["Europe"],
    Syria: ["Asia"],
    Taiwan: ["Asia"],
    Tanzania: ["Africa"],
    Thailand: ["Asia"],
    Tunisia: ["Africa"],
    Turkey: ["Asia"],
    Uganda: ["Africa"],
    Ukraine: ["Europe"],
    "United Arab Emirates": ["Asia"],
    "United Kingdom": [
      "England - Bedfordshire",
      "England - Berkshire",
      "England - Bristol",
      "England - Buckinghamshire",
      "England - Cambridgeshire",
      "England - Cheshire",
      "England - City of London",
      "England - Cornwall",
      "England - Cumbria",
      "England - Derbyshire",
      "England - Devon",
      "England - Dorset",
      "England - Durham",
      "England - East Riding of Yorkshire",
      "England - East Sussex",
      "England - Essex",
      "England - Gloucestershire",
      "England - Greater London",
      "England - Greater Manchester",
      "England - Hampshire",
      "England - Herefordshire",
      "England - Hertfordshire",
      "England - Isle of Wight",
      "England - Kent",
      "England - Lancashire",
      "England - Leicestershire",
      "England - Lincolnshire",
      "England - Merseyside",
      "England - Norfolk",
      "England - North Yorkshire",
      "England - Northamptonshire",
      "England - Northumberland",
      "England - Nottinghamshire",
      "England - Oxfordshire",
      "England - Rutland",
      "England - Shropshire",
      "England - Somerset",
      "England - South Yorkshire",
      "England - Staffordshire",
      "England - Suffolk",
      "England - Surrey",
      "England - Sussex",
      "England - Tyne & Wear",
      "England - Warwickshire",
      "England - West Midlands",
      "England - West Sussex",
      "England - West Yorkshire",
      "England - Wiltshire",
      "England - Worcestershire",
      "Northern Ireland - Antrim",
      "Northern Ireland - Armagh",
      "Northern Ireland - Down",
      "Northern Ireland - Fermanagh",
      "Northern Ireland - Londonderry",
      "Northern Ireland - Tyrone",
      "Scotland - City of Aberdeen",
      "Scotland - Aberdeenshire",
      "Scotland - Angus",
      "Scotland - Argyll",
      "Scotland - Ayrshire",
      "Scotland - Banffshire",
      "Scotland - Berwickshire",
      "Scotland - Bute",
      "Scotland - Caithness",
      "Scotland - Clackmannanshire",
      "Scotland - Dumfriesshire",
      "Scotland - Dunbartonshire",
      "Scotland - City of Dundee",
      "Scotland - East Lothian",
      "Scotland - City of Edinburgh",
      "Scotland - Fife",
      "Scotland - City of Glasgow",
      "Scotland - Inverness-shire",
      "Scotland - Kincardineshire",
      "Scotland - Kinross-shire",
      "Scotland - Kirkcudbrightshire",
      "Scotland - Lanarkshire",
      "Scotland - Midlothian",
      "Scotland - Moray",
      "Scotland - Nairnshire",
      "Scotland - Orkney",
      "Scotland - Peeblesshire",
      "Scotland - Perthshire",
      "Scotland - Renfrewshire",
      "Scotland - Ross and Cromarty",
      "Scotland - Roxburghshire",
      "Scotland - Selkirkshire",
      "Scotland - Shetland",
      "Scotland - Stirlingshire",
      "Scotland - Sutherland",
      "Scotland - West Lothian",
      "Scotland - Wigtownshire",
      "Wales - Blaenau Gwent",
      "Wales - Bridgend",
      "Wales - Caerphilly",
      "Wales - Carmarthenshire",
      "Wales - Ceredigion",
      "Wales - Swansea",
      "Wales - Cardiff",
      "Wales - Conwy",
      "Wales - Denbighshire",
      "Wales - Flintshire",
      "Wales - Gwynedd",
      "Wales - Isle of Anglesey",
      "Wales - Merthyr Tydfil",
      "Wales - Monmouthshire",
      "Wales - Neath Port Talbot",
      "Wales - Newport",
      "Wales - Pembrokeshire",
      "Wales - Powys",
      "Wales - Rhondda Cynon Taf",
      "Wales - Torfaen",
      "Wales - Vale of Glamorgan",
      "Wales - Wrexham",
    ],
    "United States": ["North America"],
    Uruguay: ["South America"],
    Uzbekistan: ["Asia"],
    Venezuela: ["South America"],
    Vietnam: ["Asia"],
    Yemen: ["Asia"],
    Zambia: ["Africa"],
    Zimbabwe: ["Africa"],
  };

  // Handle region change
  const handleRegionChange = (e) => {
    const { name, value } = e.target;

    if (name === "country_name") {
      setFormData((prev) => ({
        ...prev,
        country_name: value,
        region: regionOptions[value]?.[0] || "",
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }));
    }
  };

  // Handle input change
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;

    if (name === "retail_type") {
      setFormData((prev) => ({
        ...prev,
        retail_type: value,
        retail_sub_type: "", // Reset sub-type when type changes
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: type === "checkbox" ? checked : value,
      }));
    }

    // Clear errors
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  const handlePhoneChange = (value) => {
    setFormData((prev) => ({
      ...prev,
      phone_number: value,
    }));

    if (errors.phone_number) {
      setErrors((prev) => ({
        ...prev,
        phone_number: "",
      }));
    }
  };

  // Validation functions
  const validateStep1 = () => {
    const newErrors = {};

    if (!formData.first_name.trim())
      newErrors.first_name = "First name is required";
    else if (formData.first_name.length < 3)
      newErrors.first_name = "First name must be at least 3 characters";
    else if (formData.first_name.length > 50)
      newErrors.first_name = "First name must not exceed 50 characters";

    if (!formData.last_name.trim())
      newErrors.last_name = "Last name is required";
    else if (formData.last_name.length < 2)
      newErrors.last_name = "Last name must be at least 2 characters";
    else if (formData.last_name.length > 50)
      newErrors.last_name = "Last name must not exceed 50 characters";

    if (!formData.email.trim()) newErrors.email = "Email is required";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email))
      newErrors.email = "Please enter a valid email address";
    else if (formData.email.length > 255)
      newErrors.email = "Email must not exceed 255 characters";

    if (!formData.phone_number)
      newErrors.phone_number = "Phone number is required";
    else if (formData.phone_number.replace(/\D/g, "").length < 10)
      newErrors.phone_number = "Please enter a valid phone number";
    else if (formData.phone_number.length > 20)
      newErrors.phone_number = "Phone number must not exceed 20 characters";

    if (!formData.password) newErrors.password = "Password is required";
    else if (formData.password.length < 8)
      newErrors.password = "Password must be at least 8 characters long";
    else if (formData.password.length > 50)
      newErrors.password = "Password must not exceed 50 characters";
    else if (
      !/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]/.test(
        formData.password,
      )
    ) {
      newErrors.password =
        "Password should include uppercase, lowercase, number and special character";
    }

    if (!formData.password_confirmation)
      newErrors.password_confirmation = "Please confirm your password";
    else if (formData.password_confirmation !== formData.password)
      newErrors.password_confirmation = "Passwords do not match";

    return newErrors;
  };

  const validateStep2 = () => {
    const newErrors = {};

    if (!formData.address_line1.trim())
      newErrors.address_line1 = "Address line 1 is required";
    else if (formData.address_line1.length > 100)
      newErrors.address_line1 = "Address Line 1 must not exceed 100 characters";

    if (formData.address_line2 < 0) newErrors.address_line2 = " ";
    else if (formData.address_line2.length > 100)
      newErrors.address_line2 = "Address Line 2 must not exceed 100 characters";

    if (formData.address_line3 < 0) newErrors.address_line3 = " ";
    else if (formData.address_line3.length > 100)
      newErrors.address_line3 = "Address Line 3 must not exceed 100 characters";

    if (!formData.city.trim()) newErrors.city = "City is required";
    else if (formData.city.length > 100)
      newErrors.address_line1 = "City must not exceed 100 characters";

    if (!formData.postcode.trim())
      newErrors.postcode = "Postal code is required";
    else if (!isValidPostalCode(formData.postcode, formData.country_name))
      newErrors.postcode = "Please enter a valid postcode";
    else if (formData.postcode.length > 20)
      newErrors.postcode = "Postal code must not exceed 20 characters";

    return newErrors;
  };

  const validateStep3 = () => {
    const newErrors = {};

    if (!formData.retail_name.trim())
      newErrors.retail_name = "Retail name is required";
    else if (formData.retail_name.length > 255)
      newErrors.retail_name = "Retail name must not exceed 255 characters";

    if (!formData.retail_address.trim())
      newErrors.retail_address = "Retail address is required";
    else if (formData.retail_address.length > 255)
      newErrors.retail_address =
        "Retail address must not exceed 255 characters";

    if (!formData.retail_type.trim())
      newErrors.retail_type = "Retail type is required";
    if (!formData.retail_sub_type.trim())
      newErrors.retail_sub_type = "Retail sub-type is required";

    // if (formData.website < 0)
    //   newErrors.website = "";
    // else if (formData.website.length > 255)
    //   newErrors.website = "Website link must not exceed 255 characters";

    return newErrors;
  };

  // Check if email exists
  const checkEmailExists = async (email) => {
    try {
      const response = await fetch(
        `${API_URL}/api/check-email?email=${encodeURIComponent(email)}`,
      );
      const data = await response.json();
      return data.exists || false;
    } catch (error) {
      console.error("Error checking email:", error);
      return false;
    }
  };

  const handleNextStep = async () => {
    let stepErrors = {};

    switch (currentStep) {
      case 1:
        stepErrors = validateStep1();

        if (Object.keys(stepErrors).length === 0) {
          setValidatingFields(true);
          setMessage({
            text: "Checking email availability...",
            type: "info",
          });

          try {
            const emailExists = await checkEmailExists(formData.email);

            if (emailExists) {
              setErrors({
                email:
                  "This email is already registered. Please use a different email or login.",
              });
              setMessage({
                text: "Email already exists. Please use different credentials.",
                type: "error",
              });
              setValidatingFields(false);
              return;
            }

            setErrors({});
            setMessage({ text: "", type: "" });
            setCurrentStep((prev) => Math.min(prev + 1, totalSteps));
          } catch (error) {
            setMessage({
              text: "Error checking availability. Please try again.",
              type: "error",
            });
          } finally {
            setValidatingFields(false);
          }
        } else {
          setErrors(stepErrors);
        }
        break;

      case 2:
        stepErrors = validateStep2();
        if (Object.keys(stepErrors).length === 0) {
          setCurrentStep((prev) => Math.min(prev + 1, totalSteps));
          setErrors({});
        } else {
          setErrors(stepErrors);
        }
        break;

      case 3:
        stepErrors = validateStep3();
        if (Object.keys(stepErrors).length === 0) {
          setErrors({});
        } else {
          setErrors(stepErrors);
        }
        break;

      default:
        break;
    }
  };

  const handlePrevStep = () => {
    setCurrentStep((prev) => Math.max(prev - 1, 1));
    setErrors({});
    setMessage({ text: "", type: "" });
  };

  // Render step content
  const renderStep1 = () => (
    <>
      {/* Profile Photo Box */}
      <div style={boxStyle} className="mb-2 p-3">
        <div style={sectionTitleStyle} className="mb-0">
          Profile Photo
        </div>
        <div style={photoUploadContainerStyle}>
          {photoPreview ? (
            <div style={photoPreviewContainerStyle}>
              <img
                src={photoPreview}
                alt="Profile preview"
                style={photoPreviewStyle}
              />
              <button
                type="button"
                style={removePhotoButtonStyle}
                onClick={handleRemovePhoto}
                disabled={isLoading || validatingFields}
              >
                <FaTimes />
              </button>
            </div>
          ) : (
            <div
              style={{
                width: "120px",
                height: "120px",
                borderRadius: "50%",
                backgroundColor: "#f8f9fa",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                border: "2px dashed #dcdcdc",
                marginBottom: "12px",
                color: "#6c757d",
              }}
            >
              <FaCamera size={24} />
            </div>
          )}

          <input
            type="file"
            id="photo-upload"
            accept=".jpg,.jpeg,.png,.gif,image/jpeg,image/jpg,image/png,image/gif"
            onChange={handlePhotoUpload}
            style={{ display: "none" }}
            disabled={isLoading || validatingFields}
          />

          <label
            htmlFor="photo-upload"
            style={photoUploadButtonStyle}
            onMouseOver={(e) => {
              e.target.style.backgroundColor = "#e9ecef";
              e.target.style.borderColor = "#052C65";
            }}
            onMouseOut={(e) => {
              e.target.style.backgroundColor = "#f8f9fa";
              e.target.style.borderColor = "#dcdcdc";
            }}
          >
            <FaCamera />
            {photoPreview ? "Change Photo" : "Upload Photo"}
          </label>

          {(photoError || errors.photo) && (
            <span style={errorStyle}>{photoError || errors.photo}</span>
          )}

          <p
            style={{
              fontSize: "12px",
              color: "#6c757d",
              marginTop: "8px",
              textAlign: "center",
            }}
          >
            Supported formats: JPEG, JPG, PNG, GIF
            <br />
            Max size: 2MB
          </p>
        </div>
      </div>

      {/* Name Box */}
      <div style={boxStyle} className="mb-2 p-3">
        <div style={sectionTitleStyle} className="mb-0">
          Personal Information
        </div>
        <div style={inputRowStyle} className="gap-1 gap-md-3">
          <div style={inputRowStyleChild}>
            <label style={labelStyle}>First name</label>
            <input
              type="text"
              name="first_name"
              placeholder="John"
              value={formData.first_name}
              onChange={handleInputChange}
              style={errors.first_name ? errorInputStyle : inputStyle}
              disabled={isLoading || validatingFields}
             maxLength={255} />
            {errors.first_name && (
              <span style={errorStyle}>{errors.first_name}</span>
            )}
          </div>
          <div style={inputRowStyleChild}>
            <label style={labelStyle}>Last name</label>
            <input
              type="text"
              name="last_name"
              placeholder="Wick"
              value={formData.last_name}
              onChange={handleInputChange}
              style={errors.last_name ? errorInputStyle : inputStyle}
              disabled={isLoading || validatingFields}
             maxLength={255} />
            {errors.last_name && (
              <span style={errorStyle}>{errors.last_name}</span>
            )}
          </div>
        </div>
      </div>

      {/* Contact Box */}
      <div style={boxStyle} className="mb-2 p-3">
        <div style={sectionTitleStyle} className="mb-0">
          Contact Information
        </div>
        <div style={inputRowStyle} className="gap-1 gap-md-3">
          <div style={inputRowStyleChild}>
            <label style={labelStyle}>Email</label>
            <input
              type="email"
              name="email"
              placeholder="johnwick01@gmail.com"
              value={formData.email}
              onChange={handleInputChange}
              style={errors.email ? errorInputStyle : inputStyle}
              disabled={isLoading || validatingFields}
             maxLength={255} />
            {errors.email && <span style={errorStyle}>{errors.email}</span>}
          </div>
          <div style={inputRowStyleChild}>
            <label style={labelStyle}>Phone number</label>
            <PhoneInput
              country={"gb"}
              value={formData.phone_number}
              onChange={handlePhoneChange}
              inputStyle={
                errors.phone_number
                  ? { ...phoneInputStyle, border: "1px solid #d32f2f" }
                  : phoneInputStyle
              }
              buttonStyle={{ borderRadius: "12px 0 0 12px" }}
              disabled={isLoading || validatingFields}
            />
            {errors.phone_number && (
              <span style={errorStyle}>{errors.phone_number}</span>
            )}
          </div>
        </div>
      </div>

      {/* Security Box */}
      <div style={boxStyle} className="mb-2 p-3">
        <div style={sectionTitleStyle} className="mb-0">
          Security
        </div>
        <div style={inputRowStyle} className="gap-1 gap-md-3">
          <div style={inputRowStyleChild}>
            <label style={labelStyle}>Password</label>
            <div style={{ position: "relative" }}>
              <input
                type={formData.showPassword ? "text" : "password"}
                name="password"
                placeholder="Enter your password"
                value={formData.password}
                onChange={handleInputChange}
                style={errors.password ? errorInputStyle : inputStyle}
                disabled={isLoading || validatingFields}
               maxLength={255} />
              <span
                style={{
                  position: "absolute",
                  right: "10px",
                  top: "50%",
                  transform: "translateY(-50%)",
                  cursor: "pointer",
                }}
                onClick={() =>
                  setFormData((prev) => ({
                    ...prev,
                    showPassword: !prev.showPassword,
                  }))
                }
              >
                {formData.showPassword ? <FaEye /> : <FaEyeSlash />}
              </span>
            </div>
            {errors.password && (
              <span style={errorStyle}>{errors.password}</span>
            )}
          </div>
          <div style={inputRowStyleChild}>
            <label style={labelStyle}>Confirm Password</label>
            <div style={{ position: "relative" }}>
              <input
                type={formData.showConfirmPassword ? "text" : "password"}
                name="password_confirmation"
                placeholder="Re-enter your password"
                value={formData.password_confirmation}
                onChange={handleInputChange}
                style={
                  errors.password_confirmation ? errorInputStyle : inputStyle
                }
                disabled={isLoading || validatingFields}
               maxLength={255} />
              <span
                style={{
                  position: "absolute",
                  right: "10px",
                  top: "50%",
                  transform: "translateY(-50%)",
                  cursor: "pointer",
                }}
                onClick={() =>
                  setFormData((prev) => ({
                    ...prev,
                    showConfirmPassword: !prev.showConfirmPassword,
                  }))
                }
              >
                {formData.showConfirmPassword ? <FaEye /> : <FaEyeSlash />}
              </span>
            </div>
            {errors.password_confirmation && (
              <span style={errorStyle}>{errors.password_confirmation}</span>
            )}
          </div>
        </div>
      </div>
    </>
  );

  const renderStep2 = () => (
    <div style={boxStyle} className="mb-2 p-3">
      <div style={sectionTitleStyle} className="mb-0">
        Personal Address
      </div>

      {/* Row 1: Address line 1 and Address line 2 side by side */}
      <div style={inputRowStyle} className="gap-1 gap-md-3">
        <div style={inputRowStyleChild}>
          <label style={labelStyle}>
            Address Line 1 <span style={{ color: "red" }}>*</span>
          </label>
          <input
            type="text"
            name="address_line1"
            placeholder="Street address, P.O. box, company name"
            value={formData.address_line1}
            onChange={handleInputChange}
            style={errors.address_line1 ? errorInputStyle : inputStyle}
            disabled={isLoading}
           maxLength={255} />
          {errors.address_line1 && (
            <span style={errorStyle}>{errors.address_line1}</span>
          )}
        </div>
        <div style={inputRowStyleChild}>
          <label style={labelStyle}>Address Line 2</label>
          <input
            type="text"
            name="address_line2"
            placeholder="Apartment, suite, unit, building, floor, etc."
            value={formData.address_line2}
            onChange={handleInputChange}
            // style={inputStyle}
            style={errors.address_line2 ? errorInputStyle : inputStyle}
            disabled={isLoading}
           maxLength={255} />
          {errors.address_line2 && (
            <span style={errorStyle}>{errors.address_line2}</span>
          )}
        </div>
      </div>

      {/* Row 2: Address line 3 - full width */}
      <div style={{ marginTop: "16px" }}>
        <div style={inputRowStyleChild}>
          <label style={labelStyle}>Address Line 3</label>
          <input
            type="text"
            name="address_line3"
            placeholder="Additional address information"
            value={formData.address_line3}
            onChange={handleInputChange}
            // style={inputStyle}
            style={errors.address_line3 ? errorInputStyle : inputStyle}
            disabled={isLoading}
           maxLength={255} />
          {errors.address_line3 && (
            <span style={errorStyle}>{errors.address_line3}</span>
          )}
        </div>
      </div>

      {/* Row 3: City and Region side by side */}
      <div
        style={{ ...inputRowStyle, marginTop: "16px" }}
        className="gap-1 gap-md-3"
      >
        <div style={inputRowStyleChild}>
          <label style={labelStyle}>
            City <span style={{ color: "red" }}>*</span>
          </label>
          <input
            type="text"
            name="city"
            placeholder="City"
            value={formData.city}
            onChange={handleInputChange}
            style={errors.city ? errorInputStyle : inputStyle}
            disabled={isLoading}
           maxLength={255} />
          {errors.city && <span style={errorStyle}>{errors.city}</span>}
        </div>
        <div style={inputRowStyleChild}>
          <label style={labelStyle}>
            Region <span style={{ color: "red" }}>*</span>
          </label>
          <select
            name="region"
            value={formData.region}
            onChange={handleRegionChange}
            style={selectStyle}
            disabled={isLoading}
          >
            {(regionOptions[formData.country_name] || []).map((region) => (
              <option key={region} value={region}>
                {region}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Row 4: Post Code and Country side by side */}
      <div
        style={{ ...inputRowStyle, marginTop: "16px" }}
        className="gap-1 gap-md-3"
      >
        <div style={inputRowStyleChild}>
          <label style={labelStyle}>
            Post Code <span style={{ color: "red" }}>*</span>
          </label>
          <input
            type="text"
            name="postcode"
            placeholder="Enter postal code"
            value={formData.postcode}
            onChange={handleInputChange}
            style={errors.postcode ? errorInputStyle : inputStyle}
            disabled={isLoading}
           maxLength={20} />
          {errors.postcode && <span style={errorStyle}>{errors.postcode}</span>}
        </div>
        <div style={inputRowStyleChild}>
          <label style={labelStyle}>
            Country <span style={{ color: "red" }}>*</span>
          </label>
          <select
            name="country_name"
            value={formData.country_name}
            onChange={handleRegionChange}
            style={selectStyle}
            disabled={isLoading}
          >
            {Object.keys(regionOptions).map((country) => (
              <option key={country} value={country}>
                {country}
              </option>
            ))}
          </select>
        </div>
      </div>
    </div>
  );

  const renderStep3 = () => (
    <div style={boxStyle} className="mb-2 p-3">
      <div style={sectionTitleStyle} className="mb-0">
        Shop Details
      </div>

      {/* Retail Type Load Error Warning */}
      {typeLoadError && (
        <div
          style={{
            backgroundColor: "#fff3cd",
            border: "1px solid #ffeaa7",
            borderRadius: "8px",
            padding: "10px 15px",
            marginBottom: "16px",
            display: "flex",
            alignItems: "center",
            gap: "10px",
          }}
        >
          <FaExclamationTriangle color="#856404" />
          <span style={{ color: "#856404", fontSize: "14px" }}>
            {typeLoadError}
          </span>
        </div>
      )}

      {/* Retail Name */}
      <div style={{ marginBottom: "16px" }}>
        <label style={labelStyle}>
          Retail Shop Name <span style={{ color: "red" }}>*</span>
        </label>
        <input
          type="text"
          name="retail_name"
          placeholder="Enter retail shop name"
          value={formData.retail_name}
          onChange={handleInputChange}
          style={errors.retail_name ? errorInputStyle : inputStyle}
          disabled={isLoading}
         maxLength={255} />
        {errors.retail_name && (
          <span style={errorStyle}>{errors.retail_name}</span>
        )}
      </div>

      {/* Retail Type and Sub-Type */}
      <div style={inputRowStyle} className="gap-1 gap-md-3">
        <div style={inputRowStyleChild}>
          <label style={labelStyle}>
            Retail Type <span style={{ color: "red" }}>*</span>
          </label>
          <div style={{ position: "relative" }}>
            <select
              name="retail_type"
              value={formData.retail_type}
              onChange={handleInputChange}
              style={errors.retail_type ? errorInputStyle : selectStyle}
              disabled={isLoading || loadingTypes}
            >
              <option value="">
                {loadingTypes
                  ? "Loading retail types..."
                  : "Select retail type"}
              </option>
              {retailTypes.map((type) => (
                <option key={type} value={type}>
                  {type}
                </option>
              ))}
            </select>
            {loadingTypes && (
              <div
                style={{
                  position: "absolute",
                  right: "10px",
                  top: "50%",
                  transform: "translateY(-50%)",
                }}
              >
                <FaSpinner className="fa-spin" />
              </div>
            )}
          </div>
          {errors.retail_type && (
            <span style={errorStyle}>{errors.retail_type}</span>
          )}
        </div>

        <div style={inputRowStyleChild}>
          <label style={labelStyle}>
            Retail Sub-Type <span style={{ color: "red" }}>*</span>
          </label>
          <div style={{ position: "relative" }}>
            <select
              name="retail_sub_type"
              value={formData.retail_sub_type}
              onChange={handleInputChange}
              style={errors.retail_sub_type ? errorInputStyle : selectStyle}
              disabled={isLoading || !formData.retail_type}
            >
              <option value="">
                {!formData.retail_type
                  ? "Select retail type first"
                  : retailSubTypes.length === 0
                    ? "No sub-types available"
                    : "Select sub-type"}
              </option>
              {retailSubTypes.map((subType) => (
                <option key={subType} value={subType}>
                  {subType}
                </option>
              ))}
            </select>
          </div>
          {errors.retail_sub_type && (
            <span style={errorStyle}>{errors.retail_sub_type}</span>
          )}
        </div>
      </div>

      {/* Shop Address */}
      <div style={{ marginTop: "16px" }}>
        <label style={labelStyle}>
          Shop Address <span style={{ color: "red" }}>*</span>
        </label>
        <textarea
          name="retail_address"
          placeholder="Enter complete shop address"
          value={formData.retail_address}
          onChange={handleInputChange}
          style={{
            ...inputStyle,
            height: "80px",
            resize: "vertical",
            border: errors.retail_address
              ? "1px solid #d32f2f"
              : "1px solid #ccc",
          }}
          disabled={isLoading}
         maxLength={65535} />
        {errors.retail_address && (
          <span style={errorStyle}>{errors.retail_address}</span>
        )}
      </div>

      {/* Website and Status */}
      <div style={{ marginTop: "16px" }}>
        <div style={inputRowStyle} className="gap-1 gap-md-3">
          <div style={inputRowStyleChild}>
            <label style={labelStyle}>Website</label>
            <input
              type="text" // Changed from "url" to "text" for better UX
              name="website"
              placeholder="example.com, www.example.com, or https://example.com"
              value={formData.website}
              onChange={(e) => {
                handleInputChange(e);
                // Clear website error when user types
                if (errors.website) {
                  setErrors((prev) => ({ ...prev, website: "" }));
                }
              }}
              style={errors.website ? errorInputStyle : inputStyle}
              disabled={isLoading}
              onBlur={() => {
                // Validate website on blur
                if (formData.website && !validateWebsite(formData.website)) {
                  setErrors((prev) => ({
                    ...prev,
                    website:
                      "Please enter a valid website URL (e.g., example.com, www.example.com, https://example.com)",
                  }));
                }
              }}
             maxLength={255} />
            {errors.website && <span style={errorStyle}>{errors.website}</span>}
            {!errors.website && formData.website && (
              <span
                style={{
                  fontSize: "12px",
                  color: "#666",
                  marginTop: "4px",
                  display: "block",
                }}
              >
                Accepted formats: example.com, www.example.com,
                https://example.com
              </span>
            )}
          </div>
          <div style={inputRowStyleChild}>
            <label style={labelStyle}>Status</label>
            <select
              name="status"
              value={formData.status}
              onChange={handleInputChange}
              style={selectStyle}
              disabled={true}
            >
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>
        </div>
      </div>
    </div>
  );

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Check reCAPTCHA
    if (!captchaToken) {
      setMessage({
        text: "Please verify that you are not a robot.",
        type: "error",
      });
      return;
    }

    // Validate all steps
    const step1Errors = validateStep1();
    const step2Errors = validateStep2();
    const step3Errors = validateStep3();

    // Add website validation
    if (formData.website && !validateWebsite(formData.website)) {
      step3Errors.website =
        "Please enter a valid website URL (e.g., example.com, www.example.com, https://example.com)";
    }

    const allErrors = { ...step1Errors, ...step2Errors, ...step3Errors };

    if (Object.keys(allErrors).length > 0) {
      setErrors(allErrors);
      setMessage({
        text: "Please fix all validation errors before submitting",
        type: "error",
      });
      return;
    }

    if (!agreedToTerms) {
      setMessage({
        text: "Please accept our Privacy Policy and Terms & Conditions",
        type: "error",
      });
      return;
    }

    setIsLoading(true);
    setMessage({ text: "", type: "" });

    try {
      localStorage.setItem("tempPassword", formData.password);

      // Use the API URL without query parameters
      const apiUrl = `${API_URL}/api/retail-business/register`;

      const submitData = new FormData();

      // Format website if provided
      let formattedWebsite = formData.website;
      if (formattedWebsite) {
        // Add https:// if no protocol is specified
        if (
          !formattedWebsite.startsWith("http://") &&
          !formattedWebsite.startsWith("https://")
        ) {
          formattedWebsite = "https://" + formattedWebsite;
        }
      }

      // Append all form data (excluding UI state fields)
      Object.keys(formData).forEach((key) => {
        if (!["showPassword", "showConfirmPassword"].includes(key)) {
          const value = key === "website" ? formattedWebsite : formData[key];
          submitData.append(key, value);
        }
      });

      // Add referral code to form data if available
      if (referralCode) {
        submitData.append("referral_code", referralCode);
        console.log(
          "Adding referral code to business registration:",
          referralCode,
        );
      }

      if (photo) {
        submitData.append("photo", photo);
      }

      console.log(
        "Submitting business registration with referral code:",
        referralCode,
      );
      console.log("Retail Type:", formData.retail_type);
      console.log("Retail Sub-Type:", formData.retail_sub_type);
      console.log("Website:", formattedWebsite);
      console.log("API URL:", apiUrl);

      // Append captcha token
      submitData.append("g-recaptcha-response", captchaToken);

      const response = await fetch(apiUrl, {
        method: "POST",
        headers: {
          Accept: "application/json",
        },
        body: submitData,
      });

      const data = await response.json();

      if (response.ok) {
        setMessage({
          text: "Account created successfully! Please check your email for verification.",
          type: "success",
        });

        localStorage.setItem("verifyEmail", formData.email);

        // Clear referral code from localStorage after successful registration
        localStorage.removeItem("business_referral_code");

        setTimeout(() => {
          navigate("/verify-email-r");
        }, 2000);
      } else {
        if (data.errors) {
          const firstErrorKey = Object.keys(data.errors)[0];
          const firstError = data.errors[firstErrorKey][0];
          setMessage({
            text: firstError,
            type: "error",
          });
        } else {
          setMessage({
            text: data.message || "Failed to create account. Please try again.",
            type: "error",
          });
        }
        localStorage.removeItem("tempPassword");
      }
    } catch (error) {
      console.error("Registration error:", error);
      setMessage({
        text:
          error.message ||
          "Network error. Please check your connection and try again.",
        type: "error",
      });
      localStorage.removeItem("tempPassword");
    } finally {
      setIsLoading(false);
    }
  };

  const validateWebsite = (url) => {
    if (!url || url.trim() === "") return true; // Website is optional

    const urlStr = url.trim();

    // Very simple check: must contain a dot (.) for domain extension
    // But exclude dots at the beginning or end
    const hasDot = urlStr.includes(".");
    const dotNotAtStart = !urlStr.startsWith(".");
    const dotNotAtEnd = !urlStr.endsWith(".");
    const hasTextAfterDot = urlStr.split(".").pop().length > 0;

    // Also check it's not just "www."
    if (urlStr === "www." || urlStr === "http://" || urlStr === "https://") {
      return false;
    }

    return hasDot && dotNotAtStart && dotNotAtEnd && hasTextAfterDot;
  };

  // Styles
  const containerStyle = {
    minHeight: "100vh",
    fontFamily: "Arial, sans-serif",
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    marginTop: "30px",
  };

  const formWrapperStyle = {
    display: "flex",
    justifyContent: "center",
    padding: "0px",
    flex: 1,
    boxSizing: "border-box",
    marginTop: "120px",
    width: "100%",
    marginBottom: "120px",
  };

  const formCardStyle = {
    backgroundColor: "#ffffff",
    padding: "32px",
    borderRadius: "17.6px",
    width: "100%",
    maxWidth: "900px",
    boxShadow: "0 8px 24px rgba(0, 0, 0, 0.1)",
    boxSizing: "border-box",
  };

  const headingStyle = {
    textAlign: "center",
    fontSize: "28px",
    fontWeight: "900",
    marginBottom: "4px",
    background: "linear-gradient(90deg, #2D4CA8 52%, #9F4AFF 60%)",
    WebkitBackgroundClip: "text",
    WebkitTextFillColor: "transparent",
  };

  const subheadingStyle = {
    textAlign: "center",
    color: "#063b8a",
    fontWeight: "700",
    fontSize: "14px",
    marginBottom: "24px",
  };

  const boxStyle = {
    backgroundColor: "#ffffff",
    border: "1px solid #dcdcdc",
    borderRadius: "17.6px",
    padding: "20px",
    marginBottom: "16px",
    boxSizing: "border-box",
  };

  const sectionTitleStyle = {
    fontSize: "18px",
    fontWeight: "700",
    color: "#052C65",
    marginBottom: "12px",
  };

  const labelStyle = {
    fontWeight: "medium",
    fontSize: "14px",
    color: "#052C65",
    marginBottom: "6px",
    display: "block",
  };

  const inputRowStyle = {
    display: "flex",
    gap: "24px",
    flexWrap: "wrap",
  };

  const inputRowStyleChild = {
    flex: "1",
    minWidth: "200px",
  };

  const phoneInputStyle = {
    width: "100%",
    height: "40px",
    padding: "9px",
    borderRadius: "12px",
    border: "1px solid #ccc",
    fontSize: "14px",
    boxSizing: "border-box",
    paddingLeft: "52px",
  };

  const inputStyle = {
    width: "100%",
    height: "40px",
    padding: "9px",
    borderRadius: "12px",
    border: "1px solid #ccc",
    fontSize: "14px",
    boxSizing: "border-box",
  };

  const errorInputStyle = {
    ...inputStyle,
    border: "1px solid #d32f2f",
  };

  const selectStyle = {
    ...inputStyle,
  };

  const errorStyle = {
    color: "#d32f2f",
    fontSize: "12px",
    marginTop: "4px",
    display: "block",
  };

  // Stepper styles
  const stepperContainerStyle = {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "32px",
    position: "relative",
  };

  const stepStyle = {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    flex: 1,
    position: "relative",
    zIndex: 2,
  };

  const stepNumberStyle = (step) => ({
    width: "40px",
    height: "40px",
    borderRadius: "50%",
    backgroundColor: currentStep >= step ? "#000080" : "#e0e0e0",
    color: currentStep >= step ? "white" : "#666",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontWeight: "bold",
    fontSize: "16px",
    marginBottom: "8px",
    transition: "all 0.3s ease",
  });

  const stepLabelStyle = (step) => ({
    fontSize: "12px",
    fontWeight: currentStep === step ? "bold" : "normal",
    color: currentStep >= step ? "#000080" : "#666",
    textAlign: "center",
  });

  const progressLineStyle = {
    position: "absolute",
    top: "20px",
    left: "5%",
    right: "5%",
    height: "2px",
    backgroundColor: "#e0e0e0",
    zIndex: 1,
  };

  const progressFillStyle = {
    position: "absolute",
    top: "20px",
    left: "5%",
    height: "2px",
    backgroundColor: "#000080",
    zIndex: 1,
    transition: "width 0.3s ease",
    width: `${((currentStep - 1) / (totalSteps - 1)) * 90}%`,
  };

  // Photo upload styles
  const photoUploadContainerStyle = {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    marginBottom: "16px",
  };

  const photoPreviewStyle = {
    width: "120px",
    height: "120px",
    borderRadius: "50%",
    objectFit: "cover",
    border: "2px solid #dcdcdc",
    marginBottom: "12px",
  };

  const photoUploadButtonStyle = {
    display: "inline-flex",
    alignItems: "center",
    gap: "8px",
    padding: "10px 20px",
    backgroundColor: "#f8f9fa",
    border: "2px dashed #dcdcdc",
    borderRadius: "8px",
    cursor: "pointer",
    fontSize: "14px",
    fontWeight: "500",
    color: "#052C65",
    transition: "all 0.3s ease",
  };

  const removePhotoButtonStyle = {
    position: "absolute",
    top: "-5px",
    right: "-5px",
    backgroundColor: "#ff4444",
    color: "white",
    border: "none",
    borderRadius: "50%",
    width: "24px",
    height: "24px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    fontSize: "12px",
  };

  const photoPreviewContainerStyle = {
    position: "relative",
    display: "inline-block",
  };

  const buttonStyle = {
    width: "auto",
    padding: "12px 24px",
    backgroundColor: "#000080",
    color: "#fff",
    border: "none",
    borderRadius: "10px",
    fontWeight: "500",
    cursor: "pointer",
    fontSize: "16px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    position: "relative",
    zIndex: 10,
  };

  const loginLinkContainerStyle = {
    textAlign: "center",
    marginTop: "16px",
    fontSize: "16px",
    color: "#052C65",
  };

  const loginLinkStyle = {
    color: "#052C65",
    fontWeight: "700",
    textDecoration: "none",
    cursor: "pointer",
  };

  return (
    <div style={{ ...containerStyle, minHeight: "auto" }}>
      <Header />

      <div style={formWrapperStyle}>
        <div
          className="form-card p-3 m-3 pt-4 h-auto"
          style={{ ...formCardStyle, minHeight: "auto", maxWidth: "900px" }}
        >
          <h2 style={headingStyle}>Register Retail Business</h2>
          <p style={subheadingStyle}>
            Enter your Retail business details below to set up your account
          </p>

          {/* Stepper */}
          <div style={stepperContainerStyle}>
            <div style={progressLineStyle}></div>
            <div style={progressFillStyle}></div>

            {[1, 2, 3].map((step) => (
              <div key={step} style={stepStyle}>
                <div style={stepNumberStyle(step)}>
                  {currentStep > step ? <FaCheck size={14} /> : step}
                </div>
                <span style={stepLabelStyle(step)}>
                  {step === 1 && "Personal Info"}
                  {step === 2 && "Personal Address"}
                  {step === 3 && "Shop Details"}
                </span>
              </div>
            ))}
          </div>

          {/* Message display */}
          {message.text && (
            <div
              style={{
                textAlign: "center",
                padding: "10px",
                borderRadius: "8px",
                margin: "20px 0",
                fontWeight: "500",
                backgroundColor:
                  message.type === "error"
                    ? "#ffebee"
                    : message.type === "info"
                      ? "#e3f2fd"
                      : "#e8f5e9",
                color:
                  message.type === "error"
                    ? "#c62828"
                    : message.type === "info"
                      ? "#1565c0"
                      : "#2e7d32",
              }}
            >
              {message.text}
            </div>
          )}

          <form onSubmit={handleSubmit}>
            {currentStep === 1 && renderStep1()}
            {currentStep === 2 && renderStep2()}
            {currentStep === 3 && renderStep3()}

            {/* Navigation Buttons */}
            {currentStep < totalSteps ? (
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  marginTop: "32px",
                }}
              >
                <button
                  type="button"
                  onClick={handlePrevStep}
                  style={{
                    ...buttonStyle,
                    backgroundColor: "#6c757d",
                    opacity: currentStep === 1 ? 0.5 : 1,
                    cursor: currentStep === 1 ? "not-allowed" : "pointer",
                    height: "42px",
                  }}
                  disabled={currentStep === 1 || isLoading || validatingFields}
                >
                  <FaChevronLeft style={{ marginRight: "8px" }} />
                  Previous
                </button>

                <button
                  type="button"
                  onClick={handleNextStep}
                  style={{
                    ...buttonStyle,
                    height: "42px",
                    opacity: isLoading || validatingFields ? 0.7 : 1,
                    cursor:
                      isLoading || validatingFields ? "not-allowed" : "pointer",
                    position: "relative",
                    zIndex: 10,
                  }}
                  disabled={isLoading || validatingFields}
                >
                  {validatingFields ? "Checking..." : "Next"}
                  {!validatingFields && (
                    <FaChevronRight style={{ marginLeft: "8px" }} />
                  )}
                </button>
              </div>
            ) : (
              <div style={{ marginTop: "32px" }}>
                {/* Previous Button */}
                <div style={{ marginBottom: "20px" }}>
                  <button
                    type="button"
                    onClick={handlePrevStep}
                    style={{
                      ...buttonStyle,
                      backgroundColor: "#6c757d",
                      height: "42px",
                      position: "relative",
                      zIndex: 10,
                    }}
                    disabled={isLoading}
                  >
                    <FaChevronLeft style={{ marginRight: "8px" }} />
                    Previous
                  </button>
                </div>

                {/* Privacy Policy Checkbox - Centered */}
                <div
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    marginBottom: "20px",
                    textAlign: "center",
                    gap: "10px",
                  }}
                >
                  <input
                    type="checkbox"
                    checked={agreedToTerms}
                    onChange={(e) => setAgreedToTerms(e.target.checked)}
                    disabled={isLoading}
                  />
                  <span style={{ color: "#000000" }}>
                    By clicking continue you accept our{" "}
                    <a
                      href="/privacy-policy"
                      target="_blank"
                      style={{
                        color: "#052C65",
                        textDecoration: "none",
                        fontWeight: "bold",
                      }}
                    >
                      Privacy Policy
                    </a>{" "}
                    and{" "}
                    <a
                      href="/terms-conditions"
                      target="_blank"
                      style={{
                        color: "#052C65",
                        textDecoration: "none",
                        fontWeight: "bold",
                      }}
                    >
                      Terms & Conditions
                    </a>
                  </span>
                </div>

                {/* reCAPTCHA */}
                <div
                  style={{
                    marginBottom: "16px",
                    display: "flex",
                    alignItems: "center",
                    flexDirection: "column",
                  }}
                >
                  <ReCAPTCHA
                    sitekey={process.env.REACT_APP_RECAPTCHA_SITE_KEY} // replace with your actual site key
                    onChange={onCaptchaChange}
                  />
                  {/* { !captchaToken && message.type === "error" && message.text.includes("robot") && (
                          <span style={{ ...errorStyle, display: "block", marginTop: "8px" }}>
                              {message.text}
                          </span>
                    )} */}
                </div>

                {/* Create Account Button - Centered */}
                <div
                  style={{
                    textAlign: "center",
                    display: "flex",
                    justifyContent: "center",
                  }}
                >
                  <button
                    type="submit"
                    style={{
                      ...buttonStyle,
                      width: "auto",
                      minWidth: "200px",
                      opacity: isLoading ? 0.7 : 1,
                      cursor: isLoading ? "not-allowed" : "pointer",
                      height: "42px",
                      position: "relative",
                      zIndex: 10,
                    }}
                    disabled={isLoading}
                  >
                    {isLoading ? "Creating Account..." : "Create Account"}
                  </button>
                </div>
              </div>
            )}
          </form>

          {/* Login link */}
          <div style={loginLinkContainerStyle}>
            <span
              style={{
                color: "#444444",
                fontWeight: "semibold",
                fontSize: "16px",
              }}
            >
              Already have an account?{" "}
            </span>
            <a
              href="/work-with-tcuk"
              style={{ ...loginLinkStyle, textDecoration: "none" }}
            >
              Login here
            </a>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default CreateRetailBusiness;
